import React, { useState } from 'react';
import { Printer } from 'lucide-react';
import { fetchLotLabelsPdf } from './api';

/** The label route takes at most this many lots at once (backend/ck_ops/api.py many_pack_labels). */
export const LABELS_PER_SHEET = 50;

export function chunkLotIds(lotIds, size = LABELS_PER_SHEET) {
  const out = [];
  for (let i = 0; i < lotIds.length; i += size) out.push(lotIds.slice(i, i + size));
  return out;
}

const btn = 'inline-flex items-center justify-center gap-2 min-h-[52px] px-5 rounded-xl font-medium disabled:opacity-50 motion-organic '
  + 'border border-surface-border text-ink hover:bg-surface-sunken w-full sm:w-auto';

/**
 * "Print lot labels" on an approved invoice (H5): one pack label per lot the invoice created, from the kitchen's own
 * label route. `labels` is GET /api/review/invoices/{id} `lot_labels`: `{site_id, lot_ids}`. The tab is opened on the
 * tap, before the PDF is fetched, so a pop-up blocker sees a click rather than a late window; it is closed again if
 * the labels cannot be made, and the PDF is downloaded where the browser still refuses the tab.
 */
export default function LotLabels({ labels }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const sheets = chunkLotIds((labels && labels.lot_ids) || []);
  if (!sheets.length) return null;

  const print = async (sheet, key) => {
    setBusy(key);
    setError(null);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const blob = await fetchLotLabelsPdf(labels.site_id, sheet);
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'lot-labels.pdf';
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      setError(e.message || 'Could not make the labels.');
    } finally {
      setBusy(null);
    }
  };

  const many = sheets.length > 1;
  return (
    <>
      {sheets.map((sheet, n) => {
        const first = n * LABELS_PER_SHEET + 1;
        const range = many ? ` ${first}–${first + sheet.length - 1}` : ` (${sheet.length})`;
        return (
          <button key={first} type="button" disabled={busy !== null} onClick={() => print(sheet, n)}
            data-testid={many ? `print-lot-labels-${n + 1}` : 'print-lot-labels'} className={btn}>
            <Printer className="w-5 h-5" aria-hidden="true" /> Print lot labels{range}
          </button>
        );
      })}
      {error && <p className="w-full text-sm text-danger" role="alert" data-testid="print-lot-labels-error">{error}</p>}
    </>
  );
}
